import { Link } from "react-router-dom";
import { ArrowDown, ArrowUp, Activity, Clock, FileCheck2, Flag, ShieldAlert } from "lucide-react";

import { FilterBar } from "../components/FilterBar";
import { OfficeComparisonChart } from "../components/OfficeComparisonChart";
import { OfficeComparisonTable } from "../components/OfficeComparisonTable";
import { PageHeader } from "../components/PageHeader";
import { Sparkline } from "../components/Sparkline";
import { StatCard } from "../components/StatCard";
import { StepShell, getStep } from "../components/StepShell";
import { Badge } from "../../../ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../ui/card";
import { Skeleton } from "../../../ui/skeleton";
import { useHealthSummary } from "../lib/api/health";
import { useDashboardSearch } from "../lib/useDashboardSearch";
import { cn } from "../lib/utils";

/** Directional trend label: `invertGood` means a decrease is the good outcome. */
function TrendLabel({
  current,
  previous,
  suffix,
  invertGood = true,
}: {
  current: number;
  previous: number;
  suffix: string;
  invertGood?: boolean;
}) {
  const delta = current - previous;
  const down = delta < 0;
  const good = invertGood ? down : !down;
  const Icon = down ? ArrowDown : ArrowUp;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 text-xs font-medium tabular-nums",
        good ? "text-approve" : "text-amber",
      )}
    >
      <Icon className="size-3" />
      {Math.abs(delta).toFixed(1)}
      {suffix} vs previous period
    </span>
  );
}

const MODEL_HEALTH_STYLES: Record<"green" | "amber" | "red", string> = {
  green: "border-approve/50 bg-[color-mix(in_oklch,var(--approve)_18%,transparent)] text-approve",
  amber: "border-primary/50 bg-amber-dim text-primary",
  red: "border-decline/50 bg-[color-mix(in_oklch,var(--decline)_18%,transparent)] text-decline",
};

const MODEL_HEALTH_TEXT: Record<"green" | "amber" | "red", string> = {
  green: "Healthy",
  amber: "Watch",
  red: "Attention",
};

export function UnderwritingHealth() {
  const { range, office, compare } = useDashboardSearch();
  const { data, isPending, isError, error } = useHealthSummary({ range, office, compare });

  return (
    <StepShell step={getStep("underwriting-health")}>
      <PageHeader
        title="Underwriting Health"
        subtitle="What Dan and Mike see first: how the underwriting engine is performing right now, across every office."
      />
      <FilterBar />

      {isError ? (
        <Card>
          <CardContent className="p-6 text-sm text-decline" role="alert">
            Could not load underwriting health: {(error as Error).message}
          </CardContent>
        </Card>
      ) : isPending || !data ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-[120px] rounded-lg" />
          ))}
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="Applications Decided"
              value={data.applicationsDecided.total.toLocaleString()}
              icon={<FileCheck2 className="size-4" />}
              hint={`Approved ${data.applicationsDecided.approved.toLocaleString()} · Funded ${data.applicationsDecided.funded.toLocaleString()} · Rejected ${data.applicationsDecided.rejected.toLocaleString()}\nQTD: ${data.applicationsDecided.qtdTotal.toLocaleString()}`}
              className="whitespace-pre-line"
            />

            <StatCard
              label="First Decision Time"
              value={`${data.avgFirstDecisionTimeHours.current.toFixed(1)} hrs`}
              icon={<Clock className="size-4" />}
              trend={
                <span className="ml-auto flex flex-col items-end gap-1">
                  <Sparkline data={data.avgFirstDecisionTimeHours.sparkline} />
                  <TrendLabel
                    current={data.avgFirstDecisionTimeHours.current}
                    previous={data.avgFirstDecisionTimeHours.previousPeriod}
                    suffix=" hrs"
                  />
                </span>
              }
            />

            <StatCard
              label="Final Decision Time"
              value={`${data.avgFinalDecisionTimeHours.current.toFixed(1)} hrs`}
              icon={<Clock className="size-4" />}
              trend={
                <span className="ml-auto flex flex-col items-end gap-1">
                  <Sparkline data={data.avgFinalDecisionTimeHours.sparkline} />
                  <TrendLabel
                    current={data.avgFinalDecisionTimeHours.current}
                    previous={data.avgFinalDecisionTimeHours.previousPeriod}
                    suffix=" hrs"
                  />
                </span>
              }
            />


            <StatCard
              label="Model Health"
              value={
                <Badge
                  variant="outline"
                  className={MODEL_HEALTH_STYLES[data.modelHealth.status]}
                >
                  {MODEL_HEALTH_TEXT[data.modelHealth.status]}
                </Badge>
              }
              icon={<Activity className="size-4" />}
              hint={data.modelHealth.label}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <StatCard
              label="Exceptions This Period"
              value={(data.exceptionsCount.pg + data.exceptionsCount.corp).toLocaleString()}
              icon={<Flag className="size-4" />}
              trend={
                <span className="ml-auto">
                  <Sparkline data={data.exceptionsCount.sparkline} />
                </span>
              }
              hint={`PG ${data.exceptionsCount.pg} · Corp ${data.exceptionsCount.corp}`}
            />
            <StatCard
              label="Override Rate"
              value={`${data.overrideRate.current.toFixed(1)}%`}
              icon={<ShieldAlert className="size-4" />}
              trend={
                <span className="ml-auto flex flex-col items-end gap-1">
                  <Sparkline data={data.overrideRate.sparkline} />
                  <TrendLabel
                    current={data.overrideRate.current}
                    previous={data.overrideRate.previousPeriod}
                    suffix=" pts"
                  />
                </span>
              }
            />

          </div>

          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <CardTitle>Office Comparison</CardTitle>
                <Link
                  to="/underwriting-exceptions/manage-office-rollout?from=health"
                  className="text-sm text-primary underline-offset-4 hover:underline"
                >
                  Manage rollout →
                </Link>
              </div>
              <CardDescription>
                Pre-Athena baselines for every office, with current figures where Athena is live.
                Sorted by fastest baseline first decision time.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
              <OfficeComparisonTable rows={data.officeComparison} />
              <OfficeComparisonChart rows={data.officeComparison} />
            </CardContent>
          </Card>
        </>
      )}
    </StepShell>
  );
}
